import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { MultiSelect } from 'primereact/multiselect';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
    descargaSiniestroReporte,
    descargaSiniestroReportePorProyecto,
    recuperaConfigByCab,
    recuperaDatSiniestroPorPuesto,
    recuperaRutasDePuestos,
    recuperaRutasSiniestro,
    recuperaSimpleUbicacion,
    recuperaZonasPorPuesto
} from '../../services/Siniestro/SiniestroService';
import { listarMisRutas } from '../../services/Siniestro/RutaGuardadaService';
import { puntoCercaDeRuta, decodePolyline } from '../../util/distanciaPolilinea';
import { DataTable } from 'primereact/datatable';
import { dateTimeFromBack, dateTimeStringF1, formatToJava } from '../../util/dateUtils';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import ListaFuentes from './ListaFuentes';
import UbicacionVisor, { obtenerEstiloFallback } from './UbicacionVisor';

const parsearPoligonoWKT = (wkt) => {
    if (!wkt) return [];
    const match = wkt.match(/POLYGON\s*\(\((.+)\)\)/i);
    if (!match) return [];
    return match[1].split(',').map((par) => {
        const [lat, lng] = par.trim().split(/\s+/);
        return { lat: parseFloat(lat), lng: parseFloat(lng) };
    }).filter((p) => !isNaN(p.lat) && !isNaN(p.lng));
};

// Construye un LINESTRING WKT (formato que entiende UbicacionVisor.parsearRutaWKT)
// a partir de una ruta guardada: usa la polilinea ajustada si existe, sino la
// recta origen-destino como fallback.
const wktDeRutaGuardada = (rg) => {
    if (rg.polilineaEncoded) {
        const decoded = decodePolyline(rg.polilineaEncoded);
        if (decoded.length >= 2) {
            return 'LINESTRING (' + decoded.map(([la, ln]) => `${la} ${ln}`).join(', ') + ')';
        }
    }
    if (rg.origenLat != null && rg.origenLng != null &&
        rg.destinoLat != null && rg.destinoLng != null) {
        return `LINESTRING (${rg.origenLat} ${rg.origenLng}, ${rg.destinoLat} ${rg.destinoLng})`;
    }
    return null;
};

const puntoEnPoligono = (lat, lng, poligono) => {
    let dentro = false;
    for (let i = 0, j = poligono.length - 1; i < poligono.length; j = i++) {
        const xi = poligono[i].lat, yi = poligono[i].lng;
        const xj = poligono[j].lat, yj = poligono[j].lng;
        const intersecta = ((yi > lng) !== (yj > lng)) &&
            (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi);
        if (intersecta) dentro = !dentro;
    }
    return dentro;
};

const TextoExpandible = ({ texto, max = 80 }) => {
    const [expandido, setExpandido] = useState(false);
    if (!texto) return null;
    if (texto.length <= max) return <span>{texto}</span>;
    return (
        <div>
            {expandido ? texto : `${texto.substring(0, max)}...`}
            <span 
                className="text-primary cursor-pointer font-bold text-sm ml-1 hover:underline" 
                onClick={(e) => { e.stopPropagation(); setExpandido(!expandido); }}
            >
                {expandido ? ' Ver menos' : ' Ver más'}
            </span>
        </div>
    );
};

const UbicacionMapaII = ({ listaPuestos, versionRutasGuardadas = 0 }) => {
    const [lstZonas, setLstZonas] = useState([]);
    const [cargaZona, setCargaZona] = useState(false);
    const [lstRutasGuardadas, setLstRutasGuardadas] = useState([]);

    const fechaFin = new Date();
    const fechaIni = new Date(fechaFin);
    const diaSemana = fechaFin.getDay();
    const diferencia = diaSemana === 0 ? -6 : 1 - diaSemana;
    fechaIni.setDate(fechaFin.getDate() + diferencia);

    const [filtros, setFiltros] = useState({
        fechaInicial: fechaIni,
        fechaFinal: fechaFin,
        puestoConfId: [],
        zonaId: [],
        sectorId: [],
        rutaSeleccionada: [],
        soloPropios: false
    });
    const [lstSectores, setLstSectores] = useState([]);
    const [lstRutasDisponibles, setLstRutasDisponibles] = useState([]);

    // Lista de puestos con nombre compuesto "CLIENTE - PROYECTO - PUESTO" para el combo
    const lstPuestosConProyecto = useMemo(() => {
        return (listaPuestos || []).map((p) => ({
            ...p,
            nombreCompleto: (p.clienteNombre || '') + ' - ' + (p.proyectoNombre || '') + ' - ' + (p.puestoNombre || '')
        }));
    }, [listaPuestos]);
    const [datosPuntos, setDatosPuntos] = useState([]);
    const [datosRutas, setDatosRutas] = useState([]);
    const [datosAreas, setDatosAreas] = useState([]);
    const [lstSiniestros, setLstSiniestros] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [generando, setGenerando] = useState(false);
    const [generandoProy, setGenerandoProy] = useState(false);
    const [mostrarFuente, setMostrarFuente] = useState(false);
    const [siniestro, setSiniestro] = useState({});
    const [hoveredSiniestroId, setHoveredSiniestroId] = useState(null);
    const [selectedSiniestroId, setSelectedSiniestroId] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const toast = useRef();

    //console.log('LISTA PUESTOS..:', listaPuestos);
    const consultaSiniestros = useCallback(async (filtrosPost) => {
        if (!filtrosPost.fechaInicial) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atencion',
                detail: 'Debe haber fecha Inicio'
            });
        }
        if (!filtrosPost.fechaFinal) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atencion',
                detail: 'Debe haber fecha Final'
            });
        }
        try {
            setCargando(true);
            setDatosPuntos([]);
            setDatosRutas([]);

            const objPost = {
                fechaInicial: formatToJava(filtrosPost.fechaInicial),
                fechaFinal: formatToJava(filtrosPost.fechaFinal),
                soloPropios: filtrosPost.soloPropios
            };

            if (filtrosPost.puestoConfId && filtrosPost.puestoConfId.length > 0) {
                const lstPuestosIds = filtrosPost.puestoConfId.map((item) => item.puestoId);
                objPost['puestosIds'] = lstPuestosIds;
            } else {
                objPost['puestosIds'] = [];
            }

            if (filtrosPost.zonaId && filtrosPost.zonaId.length > 0) {
                const lstZonasId = filtrosPost.zonaId.map((item) => item.ubicacionId);
                objPost['zonasIds'] = lstZonasId;
                // El renderizado de Zonas en el mapa ahora es incondicional, se movió abajo.
            } else {
                objPost['zonasIds'] = [];
            }

            const datos = await recuperaDatSiniestroPorPuesto(objPost);
            
            // Ordenamos los incidentes de forma ascendente según su fecha y hora
            datos.sort((a, b) => {
                const dateA = dateTimeFromBack(a.fecha);
                const dateB = dateTimeFromBack(b.fecha);
                return (dateA ? dateA.getTime() : 0) - (dateB ? dateB.getTime() : 0);
            });

            //console.log('LOS DATOS SINIESTROS....: ', datos);
            const datosSiniestro = datos.map((item) => ({
                siniestroId: item.siniestroId,
                tipoSiniestroId: item.tipoSiniestroDet.id,
                tipoSiniestroCodigo: item.tipoSiniestroDet.codigo,
                tipoSiniestroNombre: item.tipoSiniestroDet.nombre,
                sectorConfigId: item.sectorConfigId,
                siniestroFecha: dateTimeStringF1(dateTimeFromBack(item.fecha)),
                descripcion: item.descripcion,
                siniestroPrivado: item.esPrivado,
                puntoGPS: item.puntoGPS,
                tipoSinParametro: item.tipoSiniestroDet.parametro,
                fuentes: item.fuentes,
                esPropio: item.esPropio ? 'Propio' : 'Terceros'
            }));

            // Filtrar por zonas seleccionadas (point-in-polygon)
            let siniestrosFiltrados = datosSiniestro;
            if (filtrosPost.zonaId && filtrosPost.zonaId.length > 0) {
                const poligonos = filtrosPost.zonaId.map((z) => parsearPoligonoWKT(z.area));
                siniestrosFiltrados = datosSiniestro.filter((item) => {
                    const [latStr, lngStr] = item.puntoGPS.split(',');
                    const lat = parseFloat(latStr);
                    const lng = parseFloat(lngStr);
                    return poligonos.some((poly) => puntoEnPoligono(lat, lng, poly));
                });
            }

            setLstSiniestros(siniestrosFiltrados);
            const listsCoord = siniestrosFiltrados.map((item) => {
                const [latStr, lngStr] = item.puntoGPS.split(',');
                const param = JSON.parse(item?.tipoSinParametro);
                return {
                    id: item.siniestroId,
                    lat: parseFloat(latStr),
                    lng: parseFloat(lngStr),
                    nombre: item.descripcion,
                    color: '#f00707',
                    tipo: 'punto',
                    icono: param?.svgIcon || '',
                    fecha: item.siniestroFecha,
                    tipoSin: item.tipoSiniestroNombre,
                    fuentes: item.fuentes,
                    esPropio: item.esPropio
                };
            });

            // Puestos a mostrar en el mapa: solo los seleccionados, o todos si no hay filtro
            const puestosFiltrados = (filtrosPost.puestoConfId && filtrosPost.puestoConfId.length > 0)
                ? listaPuestos.filter((p) => filtrosPost.puestoConfId.some((sel) => sel.puestoId === p.puestoId))
                : listaPuestos;

            const mapPuestos = puestosFiltrados.map((item) => {
                const [lat, lng] = item.puestoCoordenada.split(',');
                return {
                    id: 'puesto_' + item.puestoId,
                    lat: parseFloat(lat),
                    lng: parseFloat(lng),
                    nombre: item.proyectoNombre + ' - ' + item.puestoNombre,
                    color: `#${item.puestoColor || '1e88e5'}`,
                    tipo: 'puesto',
                    icono: '',
                    fecha: '',
                    tipoSin: 'PUESTO',
                    fuentes: []
                };
            });

            setDatosPuntos([...listsCoord, ...mapPuestos]);
        } catch (error) {
            //console.error('Error al cargar');
            console.log('ERRRO...: ', error.message);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al recuperar datos de Evento'
            });
        } finally {
            setCargando(false);
        }
    }, []);

    const descargaReportePorProyecto = useCallback(async (filtrosPost) => {
        if (!filtrosPost.fechaInicial || !filtrosPost.fechaFinal) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atencion',
                detail: 'Debe haber Fecha Inicial y Final'
            });
            return;
        }
        try {
            setGenerandoProy(true);
            const objPost = {
                fechaInicial: formatToJava(filtrosPost.fechaInicial),
                fechaFinal: formatToJava(filtrosPost.fechaFinal),
                soloPropios: filtrosPost.soloPropios
            };

            const listTodos = listaPuestos.map((item) => item.puestoId);
            objPost['todosPuestosIds'] = listTodos;

            if (filtrosPost.puestoConfId && filtrosPost.puestoConfId.length > 0) {
                objPost['puestosIds'] = filtrosPost.puestoConfId.map((item) => item.puestoId);
            } else {
                objPost['puestosIds'] = [];
            }

            if (filtrosPost.zonaId && filtrosPost.zonaId.length > 0) {
                objPost['zonasIds'] = filtrosPost.zonaId.map((item) => item.ubicacionId);
            } else {
                objPost['zonasIds'] = [];
            }

            // Proyectos: derivar de todos los puestos del usuario
            const todosProyectosIds = [...new Set(listaPuestos.map(item => item.proyectoId).filter(Boolean))];
            objPost['proyectosIds'] = todosProyectosIds;

            await descargaSiniestroReportePorProyecto(objPost);
            toast.current.show({
                severity: 'success',
                summary: 'Listo',
                detail: 'ZIP de reportes por proyecto generado'
            });
        } catch (error) {
            console.error('ERROR: al generar el ZIP de reportes por proyecto');
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo generar el ZIP de reportes por proyecto'
            });
        } finally {
            setGenerandoProy(false);
        }
    }, [listaPuestos]);

    const descargaReporte = useCallback(async (filtrosPost) => {
        if (!filtrosPost.fechaInicial) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atencion',
                detail: 'Debe haber fecha Inicio'
            });
        }
        if (!filtrosPost.fechaFinal) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atencion',
                detail: 'Debe haber fecha Final'
            });
        }
        try {
            setGenerando(true);
            const objPost = {
                fechaInicial: formatToJava(filtrosPost.fechaInicial),
                fechaFinal: formatToJava(filtrosPost.fechaFinal),
                soloPropios: filtrosPost.soloPropios
            };
            
            const listTodos = listaPuestos.map(item => item.puestoId);
            objPost['todosPuestosIds'] = listTodos;

            if (filtrosPost.puestoConfId && filtrosPost.puestoConfId.length > 0) {
                const listaSeleccionadaPuestos = filtrosPost.puestoConfId.map(item => item.puestoId)
                objPost['puestosIds'] = listaSeleccionadaPuestos
            } else {
                objPost['puestosIds'] = []
            }

            if (filtrosPost.zonaId && filtrosPost.zonaId.length > 0) {
                const listaZonas = filtrosPost.zonaId.map(item => item.ubicacionId)
                objPost['zonasIds'] = listaZonas
            } else {
                objPost['zonasIds'] = []
            }

            // Proyectos: derivar de todos los puestos del usuario
            const todosProyectosIds = [...new Set(listaPuestos.map(item => item.proyectoId).filter(Boolean))];
            objPost['proyectosIds'] = todosProyectosIds;

            await descargaSiniestroReporte(objPost);
        } catch (error) {
            console.error('ERROR: al Generar el reporte');
        } finally {
            setGenerando(false);
        }
    }, []);

    //efecto de inicio
    useEffect(() => {
        consultaSiniestros(filtros);
    }, [consultaSiniestros]);

    // Cargar rutas una sola vez al montar (no depende de filtros)
    useEffect(() => {
        const cargaRutas = async () => {
            const listTodosPuestosIds = listaPuestos.map(item => item.puestoId);
            const listTodosProyectosIds = [...new Set(listaPuestos.map(item => item.proyectoId).filter(Boolean))];
            const idsParaRutas = [...listTodosProyectosIds, ...listTodosPuestosIds];
            const coloresRutas = ['#1565C0', '#E65100', '#2E7D32', '#AD1457', '#6A1B9A', '#00838F', '#FF6F00', '#283593'];
            if (idsParaRutas.length > 0) {
                try {
                    const datRutasTotal = await recuperaRutasDePuestos(idsParaRutas);
                    const rutasUnicas = datRutasTotal.filter((item, idx, self) =>
                        idx === self.findIndex((t) => t.ubicacion?.ubicacionId === item.ubicacion?.ubicacionId)
                    );
                    const todasRutas = rutasUnicas.map((item, idx) => ({
                        __tipo: 'puesto',
                        wkt: item.area,
                        nombre: item.ubicacion?.nombre || 'Ruta ' + (idx + 1),
                        ubicacionId: item.ubicacion?.ubicacionId,
                        asociadoId: item.puestoSiniestro?.configuracionDetId,
                        puestoNombre: item.puestoSiniestro?.nombre || '',
                        busqueda: (item.ubicacion?.nombre || '') + ' ' + (item.puestoSiniestro?.nombre || ''),
                        color: coloresRutas[idx % coloresRutas.length]
                    }));
                    setLstRutasDisponibles(todasRutas);
                    setDatosRutas(todasRutas);
                } catch (error) {
                    console.error('ERROR: no se pudo cargar rutas');
                }
            }
        };
        cargaRutas();
    }, [listaPuestos]);

    // Cargar las rutas guardadas del usuario (refresca cuando el dialogo notifica cambios)
    useEffect(() => {
        const cargaRutasGuardadas = async () => {
            try {
                const datos = await listarMisRutas();
                const mapeadas = (Array.isArray(datos) ? datos : []).map((r) => ({
                    __tipo: 'guardada',
                    rutaGuardadaId: r.rutaGuardadaId,
                    ubicacionId: 'rg_' + r.rutaGuardadaId,
                    nombre: r.nombre,
                    radioKm: r.radioKm,
                    origenLat: r.origenLat,
                    origenLng: r.origenLng,
                    destinoLat: r.destinoLat,
                    destinoLng: r.destinoLng,
                    polilineaEncoded: r.polilineaEncoded,
                    siniestroGrupoId: r.siniestroGrupoId,
                    busqueda: r.nombre,
                    color: '#7C3AED'
                }));
                setLstRutasGuardadas(mapeadas);
            } catch (e) {
                console.error('ERROR: no se pudieron cargar las rutas guardadas', e);
            }
        };
        cargaRutasGuardadas();
    }, [versionRutasGuardadas]);

    // cargar la lista de Sectores y Zonas
    useEffect(() => {
        const cargaSectoresYZonas = async () => {
            try {
                const datos = await recuperaConfigByCab('SIN-0000-SECT');
                setLstSectores(datos || []);
            } catch (error) {
                console.error('ERROR: no se pudo cargar la lista de Sectores');
            }
            try {
                const zonas = await recuperaSimpleUbicacion('UBI-0000-0000-0005');
                setLstZonas(zonas || []);
            } catch (error) {
                console.error('ERROR: no se pudo cargar la lista de Zonas');
            }
        };
        cargaSectoresYZonas();
    }, []);

    // Cuando cambian las zonas seleccionadas, actualizar areas en el mapa y filtrar eventos
    useEffect(() => {
        if (filtros.zonaId && filtros.zonaId.length > 0) {
            setDatosAreas(filtros.zonaId.map((z) => ({
                area: z.area,
                nombre: z.nombre,
                descripcion: z.nombre
            })));
        } else {
            setDatosAreas([]);
        }
    }, [filtros.zonaId]);

    const handleInputChange = (e) => {
        let valueStr;
        if (e.target.name === 'soloPropios') {
            valueStr = e.value;
        } else {
            valueStr = e.target.value;
        }
        
        const nuevosFiltros = {
            ...filtros,
            [e.target.name]: valueStr
        };
        
        setFiltros(nuevosFiltros);

        // Si son los combos de "Filtros de Resultados", disparar la consulta automática
        // Si son los combos de "Filtros de Resultados" o flags que deberían ser inmediatos, disparar la consulta automática
        // Si cambia el filtro de rutas, actualizar las rutas visibles en el mapa.
        // Las rutas de puestos se pintan tal cual; las guardadas se convierten a LINESTRING
        // (desde la polilinea ajustada o, si no hay, recta origen-destino) y tambien al mapa.
        if (e.target.name === 'rutaSeleccionada') {
            if (valueStr && valueStr.length > 0) {
                const seleccionPuestos = valueStr.filter((r) => r.__tipo === 'puesto');
                const seleccionGuardadas = valueStr.filter((r) => r.__tipo === 'guardada');

                const rutasParaMapa = [];
                if (seleccionPuestos.length > 0) {
                    const idsSeleccionados = new Set(seleccionPuestos.map((r) => r.ubicacionId));
                    rutasParaMapa.push(...lstRutasDisponibles.filter((r) => idsSeleccionados.has(r.ubicacionId)));
                }
                seleccionGuardadas.forEach((rg) => {
                    const wkt = wktDeRutaGuardada(rg);
                    if (wkt) {
                        rutasParaMapa.push({ wkt, nombre: rg.nombre, color: rg.color });
                    }
                });
                setDatosRutas(rutasParaMapa);
            } else {
                setDatosRutas(lstRutasDisponibles);
            }
        }

        // Si cambia puesto, limpiar la selección de rutas
        if (e.target.name === 'puestoConfId') {
            nuevosFiltros.rutaSeleccionada = [];
        }

        if (e.target.name === 'puestoConfId' || e.target.name === 'zonaId' || e.target.name === 'soloPropios') {
            consultaSiniestros(nuevosFiltros);
        }
    };

    const handleActualizarClick = () => {
        //console.log('FILTROS...:: ', filtros);
        consultaSiniestros(filtros); // Carga manual por el usuario
    };

    const generaReporte = () => {
        descargaReporte(filtros);
    };

    const generaReportePorProyecto = () => {
        descargaReportePorProyecto(filtros);
    };

    const verFuentes = (dataSiniestro) => {
        setSiniestro(dataSiniestro);
        setMostrarFuente(true);
    };

    const actionBody = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-warning"
                    tooltip="Ver Fuentes"
                    onClick={() => verFuentes(rowData)}
                />
            </div>
        );
    };

    const tipoSiniestroBodyTemplate = (rowData) => {
        const estilo = obtenerEstiloFallback(rowData.tipoSiniestroNombre);
        return (
            <div className="flex align-items-center gap-2">
                <div style={{ backgroundColor: estilo.color, borderRadius: '6px', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="#ffffff">
                        <path d={estilo.icono} />
                    </svg>
                </div>
                <span className="font-semibold" style={{ color: '#424242' }}>{rowData.tipoSiniestroNombre}</span>
            </div>
        );
    };

    const descripcionBodyTemplate = (rowData) => {
        return <TextoExpandible texto={rowData.descripcion} />;
    };

    const headerTabla = (
        <div className="flex flex-column md:flex-row justify-content-between align-items-center mb-2">
            <h5 className="m-0 text-700 font-medium"><i className="pi pi-table mr-2"></i>Tabla de Resultados</h5>
            <span className="p-input-icon-left mt-2 md:mt-0" style={{ width: '100%', maxWidth: '250px' }}>
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Buscar en resultados..." className="w-full" />
            </span>
        </div>
    );

    return (
        <div className="surface-card p-5 shadow-3 border-round-xl text-800">
            <div className="flex align-items-center border-bottom-1 surface-border pb-3 mb-4">
               <h2 className="m-0 font-medium" style={{ color: '#0D47A1' }}><i className="pi pi-map mr-2 text-2xl"></i> Ubicación de Eventos Delictivos y Operativos de Seguridad</h2>
            </div>
            <Dialog visible={mostrarFuente} onHide={() => setMostrarFuente(false)} header="Lista de Fuentes">
                <ListaFuentes datosSin={siniestro} />
            </Dialog>
            <div className="surface-50 p-4 border-round-lg shadow-1 border-1 border-200 formgrid grid p-fluid mb-5 align-items-end">
                <Toast ref={toast} />
                <div className="col-12 mb-3">
                    <span className="text-secondary font-semibold"><i className="pi pi-calendar mr-2"></i>Parámetros de Búsqueda</span>
                </div>
                <div className="field col-12 md:col-3">
                    <label htmlFor="cboPuesto">Fecha Inicial:</label>
                    <Calendar
                        name="fechaInicial"
                        value={filtros.fechaInicial}
                        onChange={handleInputChange}
                        maxDate={filtros.fechaFinal}
                        dateFormat="dd/mm/yy"
                        showIcon
                    />
                </div>
                <div className="field col-12 md:col-3">
                    <label htmlFor="cboPuesto">Fecha Final:</label>
                    <Calendar
                        name="fechaFinal"
                        value={filtros.fechaFinal}
                        onChange={handleInputChange}
                        minDate={filtros.fechaInicial}
                        maxDate={new Date()}
                        dateFormat="dd/mm/yy"
                        showIcon
                    />
                </div>
                <div className="field col-12 md:col-2 flex align-items-end">
                    <label htmlFor="cboPuesto">Solo Propios:</label>
                    <InputSwitch checked={filtros.soloPropios} onChange={handleInputChange} name="soloPropios" />
                </div>
                <div className="field col-12 md:col-2 flex align-items-end">
                    <Button label="Consultar" icon="pi pi-search" onClick={handleActualizarClick} loading={cargando} className="p-button-outlined" />
                </div>
                <div className="field col-12 md:col-2 flex align-items-end">
                    <Button label="Reporte" icon="pi pi-file-pdf" loading={generando} onClick={generaReporte} className="p-button-outlined p-button-danger" />
                </div>
                <div className="field col-12 md:col-2 flex align-items-end">
                    <Button
                        label="Reporte por Proyecto"
                        icon="pi pi-folder-open"
                        loading={generandoProy}
                        onClick={generaReportePorProyecto}
                        className="p-button-outlined p-button-warning"
                        tooltip="Genera un ZIP con un PDF por cada proyecto"
                    />
                </div>
            </div>
            
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: '1rem',
                overflow: 'hidden'
            }}
            className="ubicacion-grid-layout">
                {/* Mapa: en desktop a la derecha, en mobile abajo */}
                <div style={{ overflow: 'hidden', minWidth: 0 }}
                     className="ubicacion-grid-mapa">
                    {cargando ? (
                        <div className="flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                            <ProgressSpinner style={{ width: '50px', height: '50px' }} animationDuration="1.s" />
                        </div>
                    ) : (
                        <UbicacionVisor
                            listaDePuntos={datosPuntos}
                            listaDeRutas={datosRutas}
                            listaDeAreas={datosAreas}
                            hoveredPuntoId={hoveredSiniestroId}
                            selectedPuntoId={selectedSiniestroId}
                        />
                    )}
                </div>
                {/* la Data */}
                {/* Tabla: en desktop a la izquierda, en mobile arriba */}
                <div style={{ overflow: 'hidden', minWidth: 0 }}
                     className="ubicacion-grid-tabla">
                    <div className="surface-border border-2 border-round p-3 shadow-2 flex flex-column bg-white">
                        
                        <div className="mb-3 border-bottom-1 surface-border pb-3 bg-indigo-50 p-3 border-round">
                            <h6 className="text-indigo-800 font-semibold mb-3 mt-0"><i className="pi pi-filter mr-2"></i>Filtros de Resultados</h6>
                            <div className="formgrid grid p-fluid">
                                <div className="field col-12 md:col-6 mb-0">
                                    <label className="text-sm font-medium text-700">Puesto:</label>
                                    <MultiSelect
                                        name="puestoConfId"
                                        options={lstPuestosConProyecto}
                                        value={filtros.puestoConfId}
                                        optionLabel="nombreCompleto"
                                        showClear
                                        onChange={handleInputChange}
                                        filter
                                        placeholder="Todos los puestos"
                                        className="w-full"
                                    />
                                </div>
                                <div className="field col-12 md:col-6 mb-0">
                                    <label className="text-sm font-medium text-700">Rutas:</label>
                                    <MultiSelect
                                        name="rutaSeleccionada"
                                        options={(() => {
                                            // Rutas de puestos: filtradas por el puesto seleccionado (o todas si no hay)
                                            let rutasPuestos;
                                            if (!filtros.puestoConfId || filtros.puestoConfId.length === 0) {
                                                rutasPuestos = lstRutasDisponibles;
                                            } else {
                                                const idsActivos = new Set();
                                                filtros.puestoConfId.forEach((p) => {
                                                    idsActivos.add(p.puestoId);
                                                    if (p.proyectoId) idsActivos.add(p.proyectoId);
                                                });
                                                rutasPuestos = lstRutasDisponibles.filter((r) => idsActivos.has(r.asociadoId));
                                            }
                                            const grupos = [];
                                            if (rutasPuestos.length > 0) {
                                                grupos.push({ label: 'De Puestos', items: rutasPuestos });
                                            }
                                            if (lstRutasGuardadas.length > 0) {
                                                grupos.push({ label: 'Mis Rutas Guardadas', items: lstRutasGuardadas });
                                            }
                                            return grupos;
                                        })()}
                                        optionGroupLabel="label"
                                        optionGroupChildren="items"
                                        value={filtros.rutaSeleccionada}
                                        onChange={handleInputChange}
                                        optionLabel="nombre"
                                        showClear
                                        filter
                                        filterBy="busqueda"
                                        placeholder="Todas las rutas"
                                        className="w-full"
                                        itemTemplate={(option) => (
                                            <div className="flex align-items-center gap-2">
                                                <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: option.color, display: 'inline-block', flexShrink: 0 }}></span>
                                                <span>{option.nombre}</span>
                                                {option.__tipo === 'puesto' && option.puestoNombre && (
                                                    <small className="text-500 ml-1">({option.puestoNombre})</small>
                                                )}
                                                {option.__tipo === 'guardada' && (
                                                    <small className="text-500 ml-1">
                                                        ({option.siniestroGrupoId ? 'grupo' : 'personal'} · {option.radioKm} km)
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                        optionGroupTemplate={(g) => (
                                            <span className="font-semibold text-700">{g.label}</span>
                                        )}
                                    />
                                </div>
                                <div className="field col-12 md:col-6 mb-0 mt-2">
                                    <label className="text-sm font-medium text-700">Sector:</label>
                                    <MultiSelect
                                        name="sectorId"
                                        options={lstSectores}
                                        value={filtros.sectorId}
                                        onChange={handleInputChange}
                                        optionLabel="nombre"
                                        showClear
                                        filter
                                        placeholder="Todos los sectores"
                                        className="w-full"
                                    />
                                </div>
                                <div className="field col-12 md:col-6 mb-0 mt-2">
                                    <label className="text-sm font-medium text-700">Zonas:</label>
                                    <MultiSelect
                                        name="zonaId"
                                        options={lstZonas}
                                        value={filtros.zonaId}
                                        onChange={handleInputChange}
                                        optionLabel="nombre"
                                        showClear
                                        filter
                                        placeholder="Todas las zonas"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={(() => {
                                let base = lstSiniestros;
                                if (filtros.sectorId && filtros.sectorId.length > 0) {
                                    base = base.filter((s) =>
                                        filtros.sectorId.some((sec) => sec.configuracionDetId === s.sectorConfigId)
                                    );
                                }
                                const rutasGuardadasSel = (filtros.rutaSeleccionada || []).filter((r) => r.__tipo === 'guardada');
                                if (rutasGuardadasSel.length > 0) {
                                    base = base.filter((s) => {
                                        const [latStr, lngStr] = (s.puntoGPS || '').split(',');
                                        const lat = parseFloat(latStr);
                                        const lng = parseFloat(lngStr);
                                        if (isNaN(lat) || isNaN(lng)) return false;
                                        return rutasGuardadasSel.some((r) => puntoCercaDeRuta(lat, lng, r));
                                    });
                                }
                                return base;
                            })()}
                            showGridlines
                            size="small"
                            paginator
                            rows={8}
                            rowsPerPageOptions={[8, 15, 25]}
                            loading={cargando}
                            loadingIcon="pi pi-spin pi-spinner"
                            selectionMode="single"
                            selection={lstSiniestros.find(s => s.siniestroId === selectedSiniestroId)}
                            onSelectionChange={(e) => setSelectedSiniestroId(e.value?.siniestroId)}
                            onRowMouseEnter={(e) => setHoveredSiniestroId(e.data?.siniestroId)}
                            onRowMouseLeave={() => setHoveredSiniestroId(null)}
                            rowClassName={() => 'cursor-pointer hover:surface-100 transition-colors transition-duration-150'}
                            globalFilter={globalFilter}
                            header={headerTabla}
                            emptyMessage={
                                <div className="flex flex-column align-items-center justify-content-center p-5 text-center">
                                    <i className="pi pi-search text-400 mb-3" style={{ fontSize: '3rem' }}></i>
                                    <strong className="text-700 text-lg">Sin ubicaciones a mostrar</strong>
                                    <p className="text-500 mt-2">No se encontraron eventos que coincidan con los filtros.</p>
                                </div>
                            }
                        >
                            <Column header="Fecha" field="siniestroFecha" sortable />
                            <Column header="Evento" field="tipoSiniestroNombre" body={tipoSiniestroBodyTemplate} sortable />
                            <Column header="Descripcion" field="descripcion" body={descripcionBodyTemplate} />
                            <Column header="Propio" field="esPropio" />
                            <Column header="Ver" body={actionBody} style={{width: '3rem'}} />
                        </DataTable>
                    </div>
                </div>
            </div>
            <style>{`
                .ubicacion-grid-mapa { order: 2; }
                .ubicacion-grid-tabla { order: 1; }
            `}</style>
        </div>
    );
};

export default UbicacionMapaII;
